import {PlatformIdentityService} from '../../../services/auth/identity/platform-identity.service';
import {Component, EventEmitter, inject, Injector, Input, OnDestroy, Output, ViewEncapsulation} from '@angular/core';
import {MaterialModule} from 'src/app/material.module';
import {Router, RouterModule} from '@angular/router';
import {CommonModule} from '@angular/common';
import {ClientService} from "../../../services/auth/client.service";
import {MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService} from "@azure/msal-angular";
import {InteractionType} from "@azure/msal-browser";
import {IClientOptions} from "../../../models/client-options.model";
import {SidebarService} from "../../../services/layout/sidebar.service";
import {ApiKeysComponent} from "../../../components/client-settings/api-keys/api-keys.component";
import {ClientSettingsComponent} from "../../../components/client-settings/client-settings.component";
import {ToastService} from "../../../services/layout/toast.service";
import {environment} from "../../../../environments/environment";
import {SelectableEntity} from "../../../services/auth/entity-selection-dialog.service";
import {ClientSelectionDialogService} from "../../../services/layout/client-selection-dialog.service";
import {ClientStateService} from "../../../services/auth/client-state.service";
import {Observable, of} from "rxjs";
import {ClientPreparationStatusComponent} from '../../../components/client-settings/client-preparation-status.component';
import {LOCAL_DEVELOPMENT_LAYOUT} from '../local-development-layout';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule, CommonModule, MaterialModule, ClientPreparationStatusComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnDestroy {
    readonly localDevelopmentLayout = inject(LOCAL_DEVELOPMENT_LAYOUT, {optional: true});
    private readonly injector = inject(Injector);
    // Resolve authenticated services only when their normal shell controls run.
    public get clientService(): ClientService { return this.injector.get(ClientService); }
    private get msalGuardConfig(): MsalGuardConfiguration { return this.injector.get(MSAL_GUARD_CONFIG, null)!; }
    private get msalService(): MsalService { return this.injector.get(MsalService, null)!; }
    private get identity(): PlatformIdentityService { return this.injector.get(PlatformIdentityService); }
    private readonly router = inject(Router);
    private get toastService(): ToastService { return this.injector.get(ToastService); }
    private get sidebarService(): SidebarService { return this.injector.get(SidebarService); }
    private get clientSelectionDialog(): ClientSelectionDialogService { return this.injector.get(ClientSelectionDialogService); }
    private get clientStateService(): ClientStateService { return this.injector.get(ClientStateService); }

    clientLoading$: Observable<boolean> = this.localDevelopmentLayout ? of(false) : this.clientStateService.clientLoading$;
  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();


  /**
   * Computed property that returns clients sorted alphabetically by name
   */
  get sortedClients(): IClientOptions[] {
    if (this.localDevelopmentLayout) return [];
    return [...this.clientService.clients].sort((a, b) => a.name.localeCompare(b.name));
  }


  ngOnDestroy(): void {
    // No subscriptions to clean up
  }

  logout() {
    if (this.localDevelopmentLayout) return;
    if (this.identity.auth0) {
      this.clientService.resetIdentityDerivedState();
      this.identity.auth0.logout().catch(() => this.toastService.showError('Sign-out could not be completed. Please try again.'));
      return;
    }
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.msalService.logoutPopup({
        account: this.msalService.instance.getActiveAccount(),
      });
    } else {
      this.msalService.logoutRedirect({
        account: this.msalService.instance.getActiveAccount(),
      });
    }
  }

  updateClient(client: IClientOptions) {
    if (this.localDevelopmentLayout) return;
    this.clientStateService.setClientLoading(true, 'route-loading');
    this.clientService.setClientAsync(client.id).then(() => {
      this.navigateToActiveProduct();
    });
  }

  openClientSettings() {
    if (this.localDevelopmentLayout) return;
    this.sidebarService.open(ClientSettingsComponent, { client: this.clientService.client });
  }

  openApiKeys() {
    if (this.localDevelopmentLayout) return;
    const currentClient = this.clientService.client;
    if (currentClient) {
      this.sidebarService.open(ApiKeysComponent, { client: currentClient });
    } else {
      this.toastService.showError('No client selected');
    }
  }

  openConnections(): void {
      if (this.localDevelopmentLayout) return;
      this.router.navigateByUrl('/connections');
  }

  openDomains(): void {
      if (this.localDevelopmentLayout) return;
      this.router.navigateByUrl('/domains');
  }

  openUsers(): void {
    if (this.localDevelopmentLayout) return;
    this.router.navigateByUrl('/settings/users');
  }

  openProviders(): void {
    if (this.localDevelopmentLayout) return;
    this.router.navigateByUrl('/connect/settings');
  }

  get isInternalAdmin(): boolean {
    if (this.localDevelopmentLayout) return false;
    return this.identity.isPlatformAdmin;
  }

    protected readonly environment = environment;

  getLogo(): string {
    if (this.localDevelopmentLayout) return '/assets/images/logos/shine.svg';
    if (this.clientService.client && this.clientService.client.logo && this.clientService.client.logo.length > 0) {
        return this.clientService.client.logo;
    }
    return "/assets/images/profile/user-1.jpg";
  }

    /**
     * Shows the client selector dialog
     * This provides a more comprehensive way to select clients with search functionality
     */
    showClientSelector(): void {
        if (this.localDevelopmentLayout) return;
        // Force a refresh of all clients to ensure we have all available clients
        this.clientService.forceRefreshClients();

        // Show a loading toast while clients are being refreshed
        this.toastService.showInfo('Loading all available clients...');

        // Wait a moment for clients to refresh before showing the dialog
        setTimeout(() => {
            if (this.clientService.clients.length === 0) {
                this.toastService.showError('No clients available');
                this.clientStateService.setClientLoading(false, 'route-loading');
                return;
            }

            console.log(`Showing client selector with ${this.clientService.clients.length} clients`);

            const selectableClients = this.getSelectableClients();

            this.clientSelectionDialog.selectClient(
                selectableClients,
                'Select Client',
                'Please select a client to continue:',
                () => this.refreshSelectableClients()
            ).subscribe(selectedClientId => {
                if (!selectedClientId) {
                    this.clientStateService.setClientLoading(false, 'route-loading');
                    return;
                }

                this.clientStateService.setClientLoading(true, 'route-loading');
                this.clientService.setClientAsync(selectedClientId).then(() => {
                    this.navigateToActiveProduct();
                }).catch(error => {
                    console.error('Failed to select client:', error);
                    this.clientStateService.setClientLoading(false, 'route-loading');
                });
            });
        }, 1000); // Wait 1 second for clients to refresh
    }

    private getSelectableClients(): SelectableEntity[] {
        if (this.localDevelopmentLayout) return [];
        return this.clientService.clients.map(client => ({
            id: client.id,
            name: client.name,
            description: client.description,
            logo: client.logo,
            enabledProducts: client.enabledProducts
        }));
    }

    private async refreshSelectableClients(): Promise<SelectableEntity[]> {
        if (this.localDevelopmentLayout) return [];
        this.clientService.refreshClientsAsync(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.getSelectableClients();
    }

  /**
   * Navigates to the page for the active product
   */
  private navigateToActiveProduct(): void {
    if (this.localDevelopmentLayout) return;
    const clientId = this.clientService.client?.id;
    const lastPath = clientId ? localStorage.getItem(`shine_last_page_${clientId}`) : null;
    const product = this.clientService.getActiveProduct();

    const navigateTo = (url: string) => {
      this.router.navigateByUrl(url).then(() => this.clientStateService.setClientLoading(false, 'route-loading'));
    };

    if (lastPath && /^\/connect\/(workflows|runs)(?:\/|\?|$)/.test(lastPath)) {
      navigateTo('/connect/integrations');
    } else if (lastPath) {
      navigateTo(lastPath);
    } else {
        switch (product) {
            case 'Shine Enrich':
                navigateTo('/enrich/settings');
                break;
            case 'Shine Connect':
                navigateTo('/connect/integrations');
                break;
            case 'Shine Performance':
                navigateTo('/performance');
                break;
            case 'Shine Webinars':
                navigateTo('/webinars');
                break;
            case 'Shine Translations':
                navigateTo('/translations');
                break;
            default:
                navigateTo('/ai/dashboard');
                break;
        }
    }
  }
}
